import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type {
  AiDraft,
  AiFeature,
  AiFlagQuery,
  AiModelInput,
  UpdateAiFeature,
  UpdateAiSettings,
} from "@/api";
import { aiApi, aiModerationApi } from "@/api";
import { aiLanguage, isDraftPending } from "@/lib/ai";
import { QUERY_KEYS } from "@/constants";

export const useAiModels = () =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_AI_MODELS],
    queryFn: () => aiApi.models(),
    select: res => res.data,
  });

export const useAiSettings = () =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_AI_SETTINGS],
    queryFn: () => aiApi.settings(),
    select: res => res.data,
  });

export const useAiUsage = (month: string) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_AI_USAGE, month],
    queryFn: () => aiApi.usage(month),
    select: res => res.data,
  });

/**
 * Model o'zgarsa sozlamalar ham qayta so'raladi: o'chirilgan model
 * funksiyalardan bo'shatiladi, `usedAs` belgisi esa sozlamaga bog'liq.
 */
const useInvalidateAi = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_AI_MODELS] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_AI_SETTINGS] });
  };
};

export const useSaveAiModel = () => {
  const { t } = useTranslation();
  const invalidate = useInvalidateAi();
  return useMutation({
    mutationFn: ({ id, dto }: { id?: number; dto: AiModelInput }) =>
      id ? aiApi.updateModel(id, dto) : aiApi.createModel(dto),
    onSuccess: () => {
      invalidate();
      toast.success(t("AI.SAVED"));
    },
  });
};

/** Yoqish/o'chirish — jimgina: holat kalitning o'zida ko'rinadi. */
export const useToggleAiModel = () => {
  const invalidate = useInvalidateAi();
  return useMutation({
    mutationFn: ({ id, enabled }: { id: number; enabled: boolean }) =>
      aiApi.updateModel(id, { enabled }),
    onSuccess: invalidate,
  });
};

export const useRemoveAiModel = () => {
  const { t } = useTranslation();
  const invalidate = useInvalidateAi();
  return useMutation({
    mutationFn: (id: number) => aiApi.removeModel(id),
    onSuccess: () => {
      invalidate();
      toast.success(t("AI.MODEL_REMOVED"));
    },
  });
};

export const useTestAiModel = () =>
  useMutation({
    mutationFn: (id: number) => aiApi.testModel(id),
  });

export const useUpdateAiSettings = () => {
  const { t } = useTranslation();
  const invalidate = useInvalidateAi();
  return useMutation({
    mutationFn: (dto: UpdateAiSettings) => aiApi.updateSettings(dto),
    onSuccess: () => {
      invalidate();
      toast.success(t("AI.SAVED"));
    },
  });
};

export const useUpdateAiFeature = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ feature, dto }: { feature: AiFeature; dto: UpdateAiFeature }) =>
      aiApi.updateFeature(feature, dto),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_AI_SETTINGS] }),
  });
};

// ─── Moderatsiya: #7 kurs tekshiruvi va #8 kontent belgilari ──────────────────

/** Qaysi AI funksiyalari yoqilgan — tugmani ko'rsatish yoki yashirish uchun. */
export const useAiPublicFeatures = () =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_AI_PUBLIC_FEATURES],
    queryFn: () => aiModerationApi.publicFeatures(),
    select: res => res.data,
    staleTime: 60_000,
  });

/** Funksiya ishlaydimi: bosh kalit VA funksiyaning o'zi yoqilgan. */
export const useAiFeatureEnabled = (feature: AiFeature): boolean => {
  const { data } = useAiPublicFeatures();
  return Boolean(data?.enabled && data.features[feature]);
};

/** Fon vazifasi tugaguncha so'rab turish oralig'i. */
export const DRAFT_POLL_MS = 3000;

const pollWhilePending = (draft: AiDraft | null | undefined) =>
  isDraftPending(draft) ? DRAFT_POLL_MS : false;

/** Kursning eng so'nggi AI tekshiruvi; tayyorlanayotgan bo'lsa — so'rab turadi. */
export const useCoursePrecheck = (courseId: number | undefined, enabled = true) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_AI_PRECHECK, courseId],
    queryFn: () => aiModerationApi.latestPrecheck(courseId as number),
    enabled: enabled && !!courseId,
    select: res => res.data,
    refetchInterval: query => pollWhilePending(query.state.data?.data),
  });

export const useRequestPrecheck = (courseId: number) => {
  const { i18n } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => aiModerationApi.requestPrecheck(courseId, aiLanguage(i18n.resolvedLanguage)),
    onSuccess: res => queryClient.setQueryData([QUERY_KEYS.GET_AI_PRECHECK, courseId], res),
  });
};

export const useAiFlags = (query: AiFlagQuery) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_AI_FLAGS, query],
    queryFn: () => aiModerationApi.flags(query),
    select: res => ({ rows: res.data, meta: res.meta }),
  });

export const useReviewAiFlag = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: "DISMISSED" | "RESOLVED" }) =>
      aiModerationApi.reviewFlag(id, status),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_AI_FLAGS] });
      toast.success(t("AI_FLAGS.REVIEWED"));
    },
  });
};
